import { supabase } from '../lib/supabase';

export interface Program {
  id: string;
  name: string;
  description: string;
  eligibility: string[];
  services: string[];
  contact: {
    phone: string;
    email: string;
    website: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  hours: {
    [key: string]: string;
  };
  languages: string[];
  distance: number; // in miles
}

export interface ProgramSearchParams {
  zipCode: string;
  radius?: number; // in miles
  services?: string[];
  languages?: string[];
  need?: 'rent' | 'food' | 'shelter' | 'legal' | 'jobs';
  language?: string; // Add language parameter
}

export const searchPrograms = async (params: ProgramSearchParams): Promise<Program[]> => {
  try {
    // Use Supabase Edge Function for AI-generated recommendations
    if (params.need) {
      const { data, error } = await supabase.functions.invoke('ai-proxy', {
        body: {
          type: 'program_recommendations',
          data: {
            zipCode: params.zipCode,
            need: params.need,
            radius: params.radius || 25,
            language: params.language || 'EN'
          }
        }
      });

      if (error) {
        console.error('Error getting AI program recommendations:', error);
        throw new Error('Failed to get program recommendations');
      }

      if (!data || !data.programs) {
        throw new Error('No programs returned from AI service');
      }

      const aiPrograms = data.programs;
      
      // Filter by languages if specified
      if (params.languages && params.languages.length > 0) {
        return aiPrograms.filter((program: Program) => 
          program.languages.some(lang => params.languages!.includes(lang))
        );
      }
      
      return aiPrograms;
    }

    // Fallback to Supabase if no need is specified
    const { data, error } = await supabase.functions.invoke('search-programs', {
      body: params
    });

    if (error) {
      console.error('Error searching programs:', error);
      throw new Error('Failed to search programs');
    }

    return data.programs;
  } catch (error) {
    console.error('Error in searchPrograms:', error);
    
    // Provide helpful fallback data when AI fails
    const fallbackPrograms = [
      {
        id: 'fallback-1',
        name: 'Community Assistance Program',
        description: 'Provides emergency assistance for various needs including food, housing, and utility support.',
        eligibility: ['Income below 200% of federal poverty level', 'Resident of service area'],
        services: ['Emergency assistance', 'Case management', 'Referral services'],
        contact: {
          phone: '(555) 123-4567',
          email: 'help@communityassist.org',
          website: 'https://communityassist.org'
        },
        address: {
          street: '123 Community Center Dr',
          city: 'Local City',
          state: 'CA',
          zip: params.zipCode
        },
        hours: {
          'Monday': '9:00 AM - 5:00 PM',
          'Tuesday': '9:00 AM - 5:00 PM',
          'Wednesday': '9:00 AM - 5:00 PM',
          'Thursday': '9:00 AM - 5:00 PM',
          'Friday': '9:00 AM - 5:00 PM'
        },
        languages: ['en', 'es'],
        distance: 1.5
      }
    ];
    
    console.warn('Using fallback programs due to AI service error:', error);
    return fallbackPrograms;
  }
};

export const getProgramDetails = async (programId: string): Promise<Program> => {
  try {
    const { data, error } = await supabase.functions.invoke('get-program-details', {
      body: { programId }
    });

    if (error) {
      console.error('Error getting program details:', error);
      throw new Error('Failed to get program details');
    }

    return data.program;
  } catch (error) {
    console.error('Error in getProgramDetails:', error);
    throw error;
  }
};

export const generateAppealLetter = async (params: {
  programId: string;
  userDetails: {
    name: string;
    address: string;
    phone: string;
    email: string;
    situation: string;
  };
}): Promise<string> => {
  try {
    const { data, error } = await supabase.functions.invoke('generate-appeal-letter', {
      body: params
    });

    if (error) {
      console.error('Error generating appeal letter:', error);
      throw new Error('Failed to generate appeal letter');
    }

    return data.letter;
  } catch (error) {
    console.error('Error in generateAppealLetter:', error);
    throw error;
  }
};

export const generateCallScript = async (params: {
  programId: string;
  userDetails: {
    name: string;
    situation: string;
    language: string;
  };
}): Promise<string> => {
  try {
    const { data, error } = await supabase.functions.invoke('generate-call-script', {
      body: params
    });

    if (error) {
      console.error('Error generating call script:', error);
      throw new Error('Failed to generate call script');
    }

    return data.script;
  } catch (error) {
    console.error('Error in generateCallScript:', error);
    throw error;
  }
}; 